import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Grid,
} from "@mui/material";
import { Login, Logout } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import configService from "../services/config";

const COOLDOWN_MS = 30 * 60 * 1000;
const MIN_COMPLETE_SECONDS = 30 * 60;

const getStoredTask = () => {
  try {
    return JSON.parse(localStorage.getItem("activeTask"));
  } catch (error) {
    return null;
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { userId, company_id } = useSelector((state) => state.globalVariables);

  const [apiData, setApiData] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  const [activeTaskId, setActiveTaskId] = useState(null);
  const [timer, setTimer] = useState(0);
  const [taskDuration, setTaskDuration] = useState(0);
  const [showComplete, setShowComplete] = useState(false);
  const [showCaptureUI, setShowCaptureUI] = useState(false);
  const [cooldownTick, setCooldownTick] = useState(0);

  const [priorityTasks, setPriorityTasks] = useState([]);
  const [workLogs, setWorkLogs] = useState([]);

  const isOut = apiData.in && !apiData.out;

  // INIT
  const initDashboard = async () => {
    setLoading(true);
    try {
      const statusRes = await configService.getTodayStatus({
        employee_id: userId,
        company_id,
      });

      if (statusRes?.status) setApiData(statusRes.data);

      setPriorityTasks([
        { id: 1, title: "Cleaning - Floor 1", deadline: "Today", priority: "High", duration: 30 },
        { id: 2, title: "Plumbing Check", deadline: "Tomorrow", priority: "Medium", duration: 60 },
        { id: 3, title: "Office Supplies", deadline: "Today", priority: "Low", duration: 15 },
        { id: 4, title: "Client Call", deadline: "Today", priority: "High", duration: 30 },
      ]);

      setWorkLogs([
        { id: 1, task: "Meeting Completed", status: "Done" },
        { id: 2, task: "Email Sent", status: "Done" },
      ]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) initDashboard();
  }, [userId]);

  // CLOCK
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCooldownTick((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // TIMER
  useEffect(() => {
    if (!activeTaskId) return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        const newTime = prev + 1;
        if (newTime >= MIN_COMPLETE_SECONDS) {
          setShowComplete(true);
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [activeTaskId]);

  const priorityColors = {
    high: "#d32f2f",   // Professional Red
    medium: "#ed6c02", // Professional Orange
    low: "#0288d1",    // Professional Blue
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  useEffect(() => {
    try {
      const storedTask = JSON.parse(localStorage.getItem("activeTask"));
      if (storedTask) {
        const elapsed = Date.now() - storedTask.startTime;
        setActiveTaskId(storedTask.id);
        setTaskDuration(storedTask.duration || 60);
        setTimer(Math.floor(elapsed / 1000));
        setShowComplete(Math.floor(elapsed / 1000) >= MIN_COMPLETE_SECONDS);
      }
    } catch (error) {
      localStorage.removeItem("activeTask");
    }
  }, [cooldownTick]);

  const handleStartTask = (task) => {
    const storedTask = getStoredTask();

    if (storedTask && storedTask.id !== task.id && Date.now() - storedTask.startTime < COOLDOWN_MS) {
      const remainingSeconds = Math.ceil((COOLDOWN_MS - (Date.now() - storedTask.startTime)) / 1000);
      toast.error(`Another task is already running. Please wait ${remainingSeconds}s before starting a new task.`);
      return;
    }

    if (activeTaskId && activeTaskId !== task.id) return;

    const startTime = Date.now();
    const taskData = {
      id: task.id,
      title: task.title,
      duration: task.duration || 60,
      startTime,
    };

    localStorage.setItem("activeTask", JSON.stringify(taskData));
    setActiveTaskId(task.id);
    setTaskDuration(taskData.duration);
    setTimer(0);
    setShowComplete(false);

    navigate("/dashboard/jobs", { state: { task } });
  };

  const handleCompleteClick = () => {
    if (timer < MIN_COMPLETE_SECONDS) {
      toast.error("Task cannot be marked complete before 30 minutes.");
      return;
    }
    setShowCaptureUI(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log("Task Completed:", file);
    localStorage.removeItem("activeTask");

    setActiveTaskId(null);
    setTimer(0);
    setTaskDuration(0);
    setShowComplete(false);
    setShowCaptureUI(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, bgcolor: "#f4f6f8", minHeight: "100vh" }}>
      <Card sx={{ bgcolor: "#ffffff", color: "#1e293b", p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>

        {/* TOP CLOCK & PUNCH SYSTEM */}
        <Stack direction="row" spacing={4} alignItems="center" justifyContent="space-around" sx={{ flexWrap: "wrap", gap: 2 }}>
          <Box sx={{
            width: 130,
            height: 130,
            borderRadius: "50%",
            border: "4px solid #1976d2",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#f8fafc"
          }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>CURRENT TIME</Typography>
            <Typography sx={{ fontFamily: "monospace", color: "#1e293b", fontWeight: "bold", fontSize: "1.1rem" }}>
              {currentTime.toLocaleTimeString()}
            </Typography>
          </Box>
          
          <Button
            onClick={() => navigate(isOut ? "/dashboard/punch-out" : "/dashboard/punch-in")}
            variant="contained"
            sx={{
              width: 130,
              height: 130,
              borderRadius: "50%",
              bgcolor: isOut ? "#e11d48" : "#16a34a",
              color: "#fff",
              flexDirection: "column",
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              "&:hover": {
                bgcolor: isOut ? "#be123c" : "#15803d",
              }
            }}
          >
            {isOut ? <Logout sx={{ fontSize: 30, mb: 0.5 }} /> : <Login sx={{ fontSize: 30, mb: 0.5 }} />}
            <Typography variant="button" sx={{ fontWeight: "bold" }}>{isOut ? "OUT" : "IN"}</Typography>
          </Button>
        </Stack>

        <Divider sx={{ my: 4, borderColor: "#e2e8f0" }} />

        {/* TASKS */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#0f172a" }}>
          Priority Tasks
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 2.5,
          }}
        >
          {priorityTasks.map((task) => {
            const color = priorityColors[task.priority.toLowerCase()] || "#64748b";

            return (
              <Card key={task.id} sx={{ bgcolor: "#ffffff", borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "0 2px 4px rgba(0,0,0,0.02)", overflow: "hidden" }}>
                <Box sx={{ height: 4, bgcolor: color }} />

                <Box sx={{ p: 2 }}>
                  <Typography sx={{ color: "#0f172a", fontWeight: "600", fontSize: "1rem", mb: 0.5 }}>
                    {task.title}
                  </Typography>

                  <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 2 }}>
                    Deadline: {task.deadline}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    {!showComplete && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleStartTask(task)}
                        disabled={(activeTaskId && activeTaskId !== task.id) || (getStoredTask() && Date.now() - getStoredTask().startTime < COOLDOWN_MS && getStoredTask().id !== task.id)}
                        sx={{ bgcolor: color, color: "#fff", fontWeight: "bold", "&:hover": { bgcolor: color } }}
                      >
                        {activeTaskId === task.id ? "Running" : "Start"}
                      </Button>
                    )}

                    {activeTaskId === task.id && !showComplete && (
                      <Typography sx={{ fontFamily: "monospace", color: "#1976d2", fontWeight: "bold" }}>
                        {formatTime(timer)}
                      </Typography>
                    )}

                    {activeTaskId === task.id && showComplete && (
                      <Button color="success" variant="contained" onClick={handleCompleteClick} sx={{ fontWeight: "bold" }}>
                        Complete
                      </Button>
                    )}
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>

        <Divider sx={{ my: 4, borderColor: "#e2e8f0" }} />

        {/* WORK LOG */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#0f172a" }}>
          Work Done Today
        </Typography>

        <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 2, border: "1px solid #e2e8f0" }}>
          {workLogs.map((log, index) => (
            <Box key={log.id}>
              <Box sx={{ display: "flex", justifyContent: "space-between", py: 1.5, px: 1 }}>
                <Typography sx={{ color: "#334155", fontWeight: "500" }}>{log.task}</Typography>
                <Typography variant="caption" sx={{ bgcolor: "#dcfce7", color: "#16a34a", px: 1.5, py: 0.5, borderRadius: 1, fontWeight: "bold" }}>
                  {log.status}
                </Typography>
              </Box>
              {index < workLogs.length - 1 && <Divider sx={{ borderColor: "#e2e8f0" }} />}
            </Box>
          ))}
        </Box>
      </Card>

      {/* CIRCULAR CAPTURE UI */}
      {showCaptureUI && (
        <Box sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bgcolor: "rgba(15, 23, 42, 0.6)",
          backdropFilter: "blur(4px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 999
        }}>
          <Box sx={{
            width: 260,
            height: 260,
            borderRadius: "50%",
            border: "4px solid #1976d2",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            bgcolor: "#ffffff",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)"
          }}>
            <Typography variant="button" sx={{ color: "#334155", fontWeight: "bold" }}>Upload Image</Typography>

            <Button
              variant="contained"
              onClick={() => document.getElementById("taskImageInput").click()}
              sx={{ fontWeight: "bold" }}
            >
              Capture / Upload
            </Button>

            <Button color="error" variant="text" onClick={() => setShowCaptureUI(false)} sx={{ fontWeight: "bold" }}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {/* HIDDEN INPUT */}
      <input
        id="taskImageInput"
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={handleImageUpload}
      />
    </Box>
  );
};

export default Dashboard;